import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Plus, Trash2, Edit2, Activity, Wifi, CheckCircle, XCircle, Loader, RotateCcw, RefreshCw } from 'lucide-react';
import { radiusServersAPI } from '../api';
import {
  Card, Button, Badge, Table, Pagination, Modal, Input, Select,
  PageHeader, Alert, useSort
} from '../components/UI';
import { useAuth } from '../context/AuthContext';

const MenuItem = ({ children, onClick, color, loading }) => (
  <button onClick={onClick} disabled={loading} style={{
    display: 'block', width: '100%', textAlign: 'left',
    padding: '9px 14px', background: 'none', border: 'none',
    cursor: loading ? 'wait' : 'pointer', fontSize: 13,
    color: color || 'var(--text-secondary)',
    transition: 'background 0.1s',
  }}
    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
    onMouseLeave={e => e.currentTarget.style.background = 'none'}
  >{children}</button>
);

const DropdownPortal = ({ anchorRef, open, children }) => {
  const [pos, setPos] = React.useState({ top: 0, left: 0 });
  React.useEffect(() => {
    if (open && anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + window.scrollY + 4, left: rect.right + window.scrollX });
    }
  }, [open, anchorRef]);
  if (!open) return null;
  return ReactDOM.createPortal(
    <div style={{ position: 'absolute', top: pos.top, left: pos.left, transform: 'translateX(-100%)', zIndex: 9999, background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow)', minWidth: 160, overflow: 'hidden' }}
      onClick={e => e.stopPropagation()}>
      {children}
    </div>,
    document.body
  );
};

const EMPTY_FORM = {
  name: '', host: '', secret: '', port: 1812, acct_port: 1813, api_port: 8080,
  db_host: '', db_name: '', db_user: '', db_password: '', db_port: 3306,
  description: '', region: '', status: 'active',
};

// ── Health check sekarang dijalankan di BACKEND (HealthCheckScheduler,
// tiap 30 detik secara default) — bukan di sini lagi. Frontend cuma:
//   1. Baca health_status/health_checked_at dkk dari field yang sudah
//      nempel di setiap row hasil GET /radius-servers (gak perlu hit
//      endpoint terpisah per server).
//   2. Sediakan tombol manual buat "cek sekarang juga" lewat
//      GET /radius-servers/:id/health?refresh=true (satu server) atau
//      POST /radius-servers/health/check-all (semua server).
// Ini menghilangkan N request polling dari browser tiap 30 detik — yang
// sebelumnya jalan terus selama tab dashboard terbuka, terlepas dari ada
// admin yang lihat atau tidak.

const RadiusMenuButton = ({ s, openMenuId, setOpenMenuId, onlyDeleted, handleRestore, checkStats, openEdit, setDeleteConfirm, setDeletePermanent, setRestartConfirm, restarting, readOnly, isSuperadmin }) => {
  const btnRef = React.useRef(null);
  if (readOnly) {
    return (
      <button onClick={() => checkStats(s)}
        style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '5px 10px', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 12, display: 'flex', alignItems: 'center', gap: 5 }}>
        📊 Stats
      </button>
    );
  }
  return (
    <div>
      <button ref={btnRef}
        onClick={e => { e.stopPropagation(); setOpenMenuId(openMenuId === s.id ? null : s.id); }}
        style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '4px 10px', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 16, lineHeight: 1 }}>
        ···
      </button>
      <DropdownPortal anchorRef={btnRef} open={openMenuId === s.id}>
        {onlyDeleted ? (
          <MenuItem color="var(--accent-green)" onClick={() => { handleRestore(s); setOpenMenuId(null); }}>↩ Restore</MenuItem>
        ) : (<>
          <MenuItem onClick={() => { checkStats(s); setOpenMenuId(null); }}>📊 Stats</MenuItem>
          <MenuItem onClick={() => { openEdit(s); setOpenMenuId(null); }}>✏️ Edit</MenuItem>
          <MenuItem color="var(--accent-orange)" loading={!!restarting[s.id]}
            onClick={() => { setRestartConfirm(s); setOpenMenuId(null); }}>
            {restarting[s.id] ? '⏳ Restarting…' : '🔄 Restart Radius'}
          </MenuItem>
        </>)}
        {(!onlyDeleted || isSuperadmin) && (<>
          <div style={{ borderTop: '1px solid var(--border)' }} />
          <MenuItem color="var(--accent-red)" onClick={() => { setDeleteConfirm(s); setDeletePermanent(false); setOpenMenuId(null); }}>🗑 Hapus</MenuItem>
        </>)}
      </DropdownPortal>
    </div>
  );
};

export default function RadiusServers() {
  const { user } = useAuth();
  const isSuperadmin = user?.role === 'superadmin';
  const readOnly = user?.role === 'admin'; // admin: lihat & cek health/stats saja, tidak bisa create/edit/delete
  const [servers, setServers] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [onlyDeleted, setOnlyDeleted] = useState(false);

  // healthChecking: id server yang sedang diproses tombol refresh manualnya
  // (cuma dipakai untuk loading-state tombol, BUKAN polling lagi)
  const [healthChecking, setHealthChecking] = useState({});
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [toasts, setToasts] = useState([]);
  const toastIdRef = useRef(0);

  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [statsModal, setStatsModal] = useState(null);
  const [statsData, setStatsData] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deletePermanent, setDeletePermanent] = useState(false);
  const [restartConfirm, setRestartConfirm] = useState(null);
  const [restarting, setRestarting] = useState({});
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [bulkDeletePermanent, setBulkDeletePermanent] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [perPage, setPerPage] = useState(15);

  useEffect(() => {
    if (!openMenuId) return;
    const handler = () => setOpenMenuId(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [openMenuId]);

  // ── Toast helpers ──────────────────────────────────────────────────────────
  const addToast = useCallback((message, type = 'success') => {
    const id = ++toastIdRef.current;
    setToasts(t => [...t, { id, message, type }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3500);
  }, []);

  // ── Load servers ───────────────────────────────────────────────────────────
  // health_status/health_db_ok/health_api_ok/health_radius_ok/health_checked_at
  // sudah nempel di setiap item dari endpoint ini (lihat Entities::RadiusServer
  // di backend) — gak perlu request tambahan buat tahu status health.
  const { sort, onSortChange, sortParam } = useSort();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: perPage };
      if (q) params.q = q;
      if (onlyDeleted) params.only_deleted = true;
      if (sortParam) params.sort = sortParam;
      const res = await radiusServersAPI.list(params);
      const allServers = res.radius_servers || [];
      const filtered = statusFilter ? allServers.filter(s => s.status === statusFilter) : allServers;
      setServers(filtered);
      setTotal(res.total || 0);
      setTotalPages(res.total_pages || 1);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [page, q, statusFilter, onlyDeleted, perPage, sortParam]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); setSelected([]); }, [onlyDeleted]);

  // ── Refresh health 1 server (manual, hit backend langsung saat itu juga) ───
  // Backend: GET /radius-servers/:id/health?refresh=true
  // (tanpa refresh=true, endpoint ini cuma balikin snapshot cache yang
  // sudah di-update background scheduler — dipakai elsewhere kalau perlu)
  const refreshHealth = useCallback(async (server) => {
    const { id, name } = server;
    setHealthChecking(m => ({ ...m, [id]: true }));
    try {
      const res = await radiusServersAPI.health(id, { refresh: true });
      setServers(list => list.map(s => s.id === id ? {
        ...s,
        health_status:     res.status,
        health_db_ok:      res.db?.connected,
        health_api_ok:     res.agent?.api,
        health_radius_ok:  res.agent?.radius,
        health_checked_at: res.checked_at,
      } : s));

      const parts = [];
      parts.push(res.db?.connected ? `DB OK (${res.db.latency_ms}ms)` : `DB ✗${res.error ? ` — ${res.error}` : ''}`);
      parts.push(res.agent?.api    ? 'API OK' : 'API ✗');
      parts.push(res.agent?.radius ? 'Radius OK' : 'Radius ✗');
      addToast(`${name} — ${parts.join(' · ')}`, res.status === 'online' ? 'success' : 'error');
    } catch (e) {
      addToast(`${name} — Gagal cek: ${e.message}`, 'error');
    } finally {
      setHealthChecking(m => ({ ...m, [id]: false }));
    }
  }, [addToast]);

  // ── Refresh health SEMUA server sekaligus ───────────────────────────────
  // Backend: POST /radius-servers/health/check-all — sinkron, jalan di
  // server (bukan N request paralel dari browser).
  const refreshAllHealth = useCallback(async () => {
    setRefreshingAll(true);
    try {
      await radiusServersAPI.checkAllHealth();
      addToast('Health check semua server selesai, memuat ulang…', 'success');
      load();
    } catch (e) {
      addToast(`Gagal refresh semua: ${e.message}`, 'error');
    } finally {
      setRefreshingAll(false);
    }
  }, [addToast, load]);

  // ── CRUD ───────────────────────────────────────────────────────────────────
  const openCreate = () => { if (readOnly) return; setEditTarget(null); setForm(EMPTY_FORM); setFormError(null); setModalOpen(true); };
  const openEdit   = (s) => { if (readOnly) return; setEditTarget(s); setForm({ ...s, db_password: '', api_port: s.api_port ?? 8080 }); setFormError(null); setModalOpen(true); };

  const handleSave = async () => {
    if (readOnly) return;
    setSaving(true); setFormError(null);
    try {
      const payload = { ...form };
      if (editTarget && !payload.db_password) delete payload.db_password;
      if (editTarget) await radiusServersAPI.update(editTarget.id, payload);
      else            await radiusServersAPI.create(payload);
      setModalOpen(false);
      setSuccess(editTarget ? 'Server diperbarui.' : 'Server ditambahkan.');
      load();
    } catch (e) { setFormError(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (readOnly) return;
    const permanent = isSuperadmin && (onlyDeleted || deletePermanent);
    try {
      if (permanent) await radiusServersAPI.deletePermanent(deleteConfirm.id);
      else await radiusServersAPI.delete(deleteConfirm.id);
      setDeleteConfirm(null); setDeletePermanent(false);
      setSuccess(permanent ? 'Server dihapus permanen.' : `Server "${deleteConfirm.name}" dihapus.`);
      setSelected(sel => sel.filter(id => id !== deleteConfirm.id));
      load();
    } catch (e) { setError(e.message); setDeleteConfirm(null); }
  };

  const handleRestore = async (s) => {
    if (readOnly) return;
    try { await radiusServersAPI.restore(s.id); setSuccess('Server dipulihkan.'); load(); }
    catch (e) { setError(e.message); }
  };

  // ── Restart FreeRADIUS via agent ───────────────────────────────────────────
  // Backend: POST /radius-servers/:id/restart (RadiusAgentService -> Go agent)
  const handleRestart = async () => {
    if (readOnly || !restartConfirm) return;
    const s = restartConfirm;
    setRestartConfirm(null);
    setRestarting(m => ({ ...m, [s.id]: true }));
    try {
      const res = await radiusServersAPI.restart(s.id);
      if (res.success === false) {
        addToast(`${s.name} — Gagal restart: ${res.error || 'Tidak diketahui'}`, 'error');
      } else {
        addToast(`${s.name} — FreeRADIUS berhasil di-restart.`, 'success');
      }
      refreshHealth(s);
    } catch (e) {
      addToast(`${s.name} — Gagal restart: ${e.message}`, 'error');
    } finally {
      setRestarting(m => ({ ...m, [s.id]: false }));
    }
  };

  const handleBulkDelete = async () => {
    if (readOnly) return;
    const permanent = isSuperadmin && (onlyDeleted || bulkDeletePermanent);
    try {
      if (permanent) await Promise.all(selected.map(id => radiusServersAPI.deletePermanent(id)));
      else await Promise.all(selected.map(id => radiusServersAPI.delete(id)));
      setSuccess(`${selected.length} server ${permanent ? 'dihapus permanen' : 'dihapus'}.`);
      setSelected([]); setBulkDeleteConfirm(false); setBulkDeletePermanent(false); load();
    } catch (e) { setError(e.message); setBulkDeleteConfirm(false); }
  };

  const handleBulkRestore = async () => {
    if (readOnly) return;
    try {
      await Promise.all(selected.map(id => radiusServersAPI.restore(id)));
      setSuccess(`${selected.length} server dipulihkan.`); setSelected([]); load();
    } catch (e) { setError(e.message); }
  };

  const checkStats = async (s) => {
    setStatsModal(s); setStatsData(null); setStatsLoading(true);
    try { setStatsData(await radiusServersAPI.stats(s.id)); }
    catch (e) { setStatsData({ error: e.message }); }
    finally { setStatsLoading(false); }
  };

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // ── Health indicator cell ──────────────────────────────────────────────────
  // Status dibaca dari field health_status yang sudah ikut di response list
  // (diisi terakhir kali oleh background scheduler, biasanya <30s basi).
  // Klik = trigger refresh manual saat itu juga (lihat refreshHealth).
  const HealthCell = ({ server }) => {
    const checking = !!healthChecking[server.id];
    const h = server.health_status; // 'online' | 'degraded' | 'offline' | 'unknown' (default)
    const states = {
      online:   { color: 'var(--accent-green)',  label: 'Online',   dot: 'var(--accent-green)'  },
      degraded: { color: 'var(--accent-orange)', label: 'Degraded', dot: 'var(--accent-orange)' },
      offline:  { color: 'var(--accent-red)',    label: 'Offline',  dot: 'var(--accent-red)'    },
    };
    const s = states[h] || { color: 'var(--text-muted)', label: 'Belum dicek', dot: 'var(--text-muted)' };
    const checkedLabel = server.health_checked_at
      ? new Date(server.health_checked_at).toLocaleTimeString()
      : null;

    return (
      <button
        onClick={() => refreshHealth(server)}
        disabled={checking}
        title={checkedLabel ? `Terakhir dicek ${checkedLabel} — klik untuk cek ulang sekarang` : 'Klik untuk cek sekarang'}
        style={{ background: 'none', border: 'none', cursor: checking ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px', borderRadius: 'var(--radius)', transition: 'background 0.15s' }}
        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-card-hover)'}
        onMouseLeave={e => e.currentTarget.style.background = 'none'}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: s.color }}>
          {checking
            ? <Loader size={12} style={{ animation: 'spin 0.7s linear infinite' }} />
            : <span style={{
                width: 8, height: 8, borderRadius: '50%',
                background: s.dot,
                boxShadow: h === 'online' ? `0 0 6px ${s.dot}` : 'none',
                display: 'inline-block',
                animation: h === 'online' ? 'pulse 2s infinite' : 'none',
              }} />
          }
          {s.label}
        </span>
      </button>
    );
  };

  // ── Format health_uptime_seconds jadi "3d 12:12:30" ──────────────────────────
  const formatUptime = (seconds) => {
    if (seconds === null || seconds === undefined) return null;
    let s = Math.max(0, Math.floor(seconds));
    const d = Math.floor(s / 86400); s -= d * 86400;
    const h = Math.floor(s / 3600);  s -= h * 3600;
    const m = Math.floor(s / 60);    s -= m * 60;
    const pad = n => String(n).padStart(2, '0');
    const clock = `${pad(h)}:${pad(m)}:${pad(s)}`;
    return d > 0 ? `${d}d ${clock}` : clock;
  };

  const UptimeCell = ({ server }) => {
    if (onlyDeleted || server.health_status !== 'online' || !server.health_uptime_seconds) {
      return <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>—</span>;
    }
    const label = formatUptime(server.health_uptime_seconds);
    const startedLabel = server.health_started_at ? new Date(server.health_started_at).toLocaleString() : null;
    return (
      <span
        className="mono"
        title={startedLabel ? `Berjalan sejak ${startedLabel}` : undefined}
        style={{ fontSize: 12, color: 'var(--text-secondary)' }}
      >
        {label}
      </span>
    );
  };

  const columns = [
    { key: 'name',   label: 'Name',   sortable: true, render: v => <span className="mono" style={{ color: 'var(--text-mono)', fontSize: 12 }}>{v}</span> },
    { key: 'host',   label: 'Host',   sortable: true, render: v => <span className="mono" style={{ fontSize: 12 }}>{v}</span> },
    { key: 'port',   label: 'Ports',  render: (v, r) => <span className="mono" style={{ fontSize: 11, color: 'var(--text-muted)' }}>{v}/{r.acct_port}{r.api_port ? `/${r.api_port}` : ''}</span> },
    { key: 'region', label: 'Region', sortable: true, render: v => v ? <span style={{ fontSize: 11, padding: '2px 6px', background: 'var(--bg-input)', borderRadius: 4 }}>{v}</span> : '—' },
    { key: 'status', label: 'Status', sortable: true, render: v => <Badge status={v} /> },
    { key: 'health', label: 'Health', render: (_, s) => onlyDeleted ? <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>—</span> : <HealthCell server={s} /> },
    { key: 'uptime', label: 'Uptime', render: (_, s) => <UptimeCell server={s} /> },
    {
      key: 'id', label: '', render: (_, s) => (
        <RadiusMenuButton s={s} openMenuId={openMenuId} setOpenMenuId={setOpenMenuId} onlyDeleted={onlyDeleted} handleRestore={handleRestore} checkStats={checkStats} openEdit={openEdit} setDeleteConfirm={setDeleteConfirm} setDeletePermanent={setDeletePermanent} setRestartConfirm={setRestartConfirm} restarting={restarting} readOnly={readOnly} isSuperadmin={isSuperadmin} />
      )
    },
  ];

  return (
    <div className="animate-in">
      {/* Toast container */}
      <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 8, pointerEvents: 'none' }}>
        {toasts.map(t => (
          <div key={t.id} className="animate-in" style={{
            padding: '10px 16px', borderRadius: 'var(--radius-lg)',
            background: t.type === 'success' ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)',
            border: `1px solid ${t.type === 'success' ? 'rgba(16,185,129,0.4)' : 'rgba(239,68,68,0.4)'}`,
            color: t.type === 'success' ? 'var(--accent-green)' : 'var(--accent-red)',
            fontSize: 13, fontWeight: 500,
            boxShadow: 'var(--shadow)', backdropFilter: 'blur(8px)',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            {t.type === 'success' ? <CheckCircle size={14} /> : <XCircle size={14} />}
            {t.message}
          </div>
        ))}
      </div>

      <PageHeader
        title="Radius Servers"
        subtitle={`${total} server${onlyDeleted ? ' (terhapus)' : ' — health dicek otomatis di background tiap 30 detik'}`}
        actions={
          readOnly ? null : (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {!onlyDeleted && (
                <Button variant="ghost" onClick={refreshAllHealth} loading={refreshingAll}>
                  <RefreshCw size={13} /> Refresh Semua Health
                </Button>
              )}
              {selected.length > 0 && (<>
                {onlyDeleted && <Button variant="success" onClick={handleBulkRestore}><RotateCcw size={13} /> Restore {selected.length}</Button>}
                {(!onlyDeleted || isSuperadmin) && (
                  <Button variant="danger" onClick={() => setBulkDeleteConfirm(true)}>
                    <Trash2 size={13} /> Hapus {selected.length} server
                  </Button>
                )}
              </>)}
              <Button variant={onlyDeleted ? 'secondary' : 'ghost'} onClick={() => setOnlyDeleted(d => !d)}>
                {onlyDeleted ? 'Tampil Aktif' : '🗑 Terhapus'}
              </Button>
              <Button onClick={openCreate}><Plus size={14} /> Tambah Server</Button>
            </div>
          )
        }
      />

      {error   && <Alert type="error"   message={error}   onClose={() => setError(null)}   />}
      {success && <Alert type="success" message={success} onClose={() => setSuccess(null)} />}

      {/* Search & Filter */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input placeholder="Cari server…" value={q} onChange={e => { setQ(e.target.value); setPage(1); }}
          style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '8px 14px', color: 'var(--text-primary)', fontSize: 13, outline: 'none', fontFamily: 'var(--font-sans)', width: 220 }} />
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '8px 12px', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer', outline: 'none' }}>
          <option value="">Semua Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      <Card style={{ padding: 0 }}>
        <Table columns={columns} data={servers} loading={loading} sort={sort} onSortChange={onSortChange}
          emptyMessage={onlyDeleted ? 'Tidak ada data terhapus' : 'Belum ada radius server'}
          selected={readOnly ? undefined : selected} onSelect={readOnly ? undefined : setSelected} />
        <div style={{ padding: '0 16px' }}>
          <Pagination page={page} totalPages={totalPages} total={total} onChange={setPage} perPage={perPage} onPerPageChange={n => { setPerPage(n); setPage(1); }} />
        </div>
      </Card>

      {/* Create / Edit Modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)}
        title={editTarget ? 'Edit Radius Server' : 'Tambah Radius Server'} width={600}>
        {formError && <Alert type="error" message={formError} />}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Input label="Name *"    value={form.name}    onChange={e => f('name', e.target.value)}    placeholder="radius-jakarta-01" />
          <Input label="Host *"    value={form.host}    onChange={e => f('host', e.target.value)}    placeholder="192.168.1.10" />
          <Input label="Auth Port" type="number" value={form.port}      onChange={e => f('port', +e.target.value)} />
          <Input label="Acct Port" type="number" value={form.acct_port} onChange={e => f('acct_port', +e.target.value)} />
          <Input label="API Port" type="number" value={form.api_port} onChange={e => f('api_port', +e.target.value)} />
          <Input label="Secret *"  value={form.secret}  onChange={e => f('secret', e.target.value)}  type="password" placeholder="radius shared secret" />
          <Input label="Region"    value={form.region}  onChange={e => f('region', e.target.value)}  placeholder="jakarta" />
          <div style={{ gridColumn: '1/-1' }}>
            <Input label="Description" value={form.description} onChange={e => f('description', e.target.value)} />
          </div>
          <div style={{ gridColumn: '1/-1', borderTop: '1px solid var(--border)', paddingTop: 12, fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>DATABASE CONNECTION</div>
          <Input label="DB Host *"     value={form.db_host}     onChange={e => f('db_host', e.target.value)}     placeholder="localhost" />
          <Input label="DB Port"       type="number" value={form.db_port} onChange={e => f('db_port', +e.target.value)} />
          <Input label="DB Name *"     value={form.db_name}     onChange={e => f('db_name', e.target.value)}     placeholder="radius" />
          <Input label="DB User *"     value={form.db_user}     onChange={e => f('db_user', e.target.value)}     placeholder="radius_user" />
          <Input label="DB Password *" type="password" value={form.db_password} onChange={e => f('db_password', e.target.value)} placeholder={editTarget ? '(kosong = tidak berubah)' : ''} />
          <Select label="Status" value={form.status} onChange={e => f('status', e.target.value)}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </Select>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <Button variant="ghost" onClick={() => setModalOpen(false)}>Batal</Button>
          <Button onClick={handleSave} loading={saving}>{editTarget ? 'Simpan' : 'Tambah Server'}</Button>
        </div>
      </Modal>

      {/* Stats Modal */}
      <Modal open={!!statsModal} onClose={() => setStatsModal(null)} title={`Accounting Stats — ${statsModal?.name}`} width={440}>
        {statsLoading ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <div style={{ width: 32, height: 32, border: '2px solid var(--accent-cyan)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite', margin: '0 auto' }} />
          </div>
        ) : statsData && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {statsData.error ? <Alert type="error" message={statsData.error} /> :
              Object.entries(statsData).map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: 'var(--bg-input)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{k}</span>
                  <span className="mono" style={{ fontSize: 12, color: 'var(--accent-cyan)' }}>{String(v)}</span>
                </div>
              ))
            }
          </div>
        )}
      </Modal>

      <Modal open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} title="Hapus Server" width={380}>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16 }}>
          Yakin ingin menghapus server <strong style={{ color: 'var(--accent-red)' }}>{deleteConfirm?.name}</strong>?
        </p>
        {isSuperadmin && (onlyDeleted ? (
          <p style={{ fontSize: 12, color: 'var(--accent-red)', marginBottom: 20 }}>
            ⚠️ Server ini sudah berada di daftar terhapus, sehingga akan dihapus permanen dan tidak bisa dipulihkan lagi.
          </p>
        ) : (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20, cursor: 'pointer' }}>
            <input type="checkbox" checked={deletePermanent} onChange={e => setDeletePermanent(e.target.checked)} style={{ accentColor: 'var(--accent-red)' }} />
            <span>Hapus permanen <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>(tidak bisa dipulihkan)</span></span>
          </label>
        ))}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>Batal</Button>
          <Button variant="danger" onClick={handleDelete}>Hapus</Button>
        </div>
      </Modal>

      {/* Bulk delete */}
      <Modal open={bulkDeleteConfirm} onClose={() => setBulkDeleteConfirm(false)} title="Hapus Bulk Server" width={380}>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16 }}>
          Yakin ingin menghapus <strong style={{ color: 'var(--accent-red)' }}>{selected.length} server</strong> sekaligus?
        </p>
        {isSuperadmin && (onlyDeleted ? (
          <p style={{ fontSize: 12, color: 'var(--accent-red)', marginBottom: 20 }}>
            ⚠️ Server ini sudah berada di daftar terhapus, sehingga akan dihapus permanen dan tidak bisa dipulihkan lagi.
          </p>
        ) : (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20, cursor: 'pointer' }}>
            <input type="checkbox" checked={bulkDeletePermanent} onChange={e => setBulkDeletePermanent(e.target.checked)} style={{ accentColor: 'var(--accent-red)' }} />
            <span>Hapus permanen <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>(tidak bisa dipulihkan)</span></span>
          </label>
        ))}
      </Modal>  
      {/* Restart confirm */}
      <Modal open={!!restartConfirm} onClose={() => setRestartConfirm(null)} title="Restart FreeRADIUS" width={380}>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 8 }}>
          Yakin ingin restart FreeRADIUS di server <strong style={{ color: 'var(--accent-orange)' }}>{restartConfirm?.name}</strong>?
        </p>
        <p style={{ color: 'var(--text-muted)', fontSize: 12, marginBottom: 20 }}>
          Sesi auth/acct yang sedang berjalan di server ini bisa terganggu sesaat selama proses restart.
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={() => setRestartConfirm(null)}>Batal</Button>
          <Button variant="danger" onClick={handleRestart}>Restart</Button>
        </div>
      </Modal>
    </div>
  );
}

